import { useEffect, useMemo, useState } from 'react';
import { adminApi } from './api';
import './referrals.css';

export default function ReferralManager({ onUnauthorized }) {
  const [initial, setInitial] = useState(null);
  const [form, setForm] = useState(null);
  const [stats, setStats] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let current = true;
    Promise.all([adminApi.referralSettings(), adminApi.referralCatalog()])
      .then(([result, available]) => {
        if (!current) return;
        const raw = result.settings;
        const normalized = {
          enabled: raw.enabled,
          requiredSignups: raw.required_signups,
          rewardDays: raw.reward_days,
          scope: raw.reward_scope,
          repeatable: raw.repeatable,
          selectedItems: raw.selected_items || [],
        };
        setInitial(normalized);
        setForm(normalized);
        setStats(result.stats);
        setCatalog(available);
      })
      .catch((err) => {
        if (!current) return;
        setError(err.message);
        if (err.status === 401) onUnauthorized();
      });
    return () => { current = false; };
  }, [onUnauthorized]);

  const filtered = useMemo(() => catalog.filter((item) =>
    (item.category + ' ' + item.title).toLocaleLowerCase('fr-FR')
      .includes(query.toLocaleLowerCase('fr-FR'))), [catalog, query]);

  function setField(name, value) {
    setForm((old) => ({ ...old, [name]: value }));
    setNotice('');
  }

  function toggleItem(key, checked) {
    setForm((old) => ({
      ...old,
      selectedItems: checked
        ? [...new Set([...old.selectedItems, key])]
        : old.selectedItems.filter((item) => item !== key),
    }));
  }

  async function save(event) {
    event.preventDefault();
    setError('');
    setNotice('');
    if (!Number.isInteger(form.requiredSignups) || form.requiredSignups < 1 || form.requiredSignups > 1000
      || !Number.isInteger(form.rewardDays) || form.rewardDays < 1 || form.rewardDays > 365) {
      setError('Les valeurs numériques sont invalides.');
      return;
    }
    if (form.scope === 'selected' && !form.selectedItems.length) {
      setError('Sélectionne au moins un contenu Premium.');
      return;
    }
    try {
      setBusy(true);
      const result = await adminApi.updateReferralSettings(form);
      const saved = {
        enabled: result.settings.enabled,
        requiredSignups: result.settings.required_signups,
        rewardDays: result.settings.reward_days,
        scope: result.settings.reward_scope,
        repeatable: result.settings.repeatable,
        selectedItems: result.settings.selected_items,
      };
      setInitial(saved);
      setForm(saved);
      setNotice('Paramètres enregistrés. Les récompenses déjà obtenues restent inchangées.');
    } catch (err) {
      setError(err.message || 'Enregistrement impossible.');
      if (err.status === 401) onUnauthorized();
    } finally { setBusy(false); }
  }

  if (!form) return (
    <section className="bo-panel">
      <h2>Parrainage et récompenses</h2>
      {error ? <p role="alert" className="bo-referral-error">{error}</p> : <p>Chargement…</p>}
    </section>
  );

  return (
    <section className="bo-referrals">
      <div className="bo-panel">
        <p className="bo-eyebrow">CROISSANCE COMMUNAUTAIRE</p>
        <h2>Parrainage direct</h2>
        <p className="bo-muted">
          Un candidat reçoit une récompense après des inscriptions directes avec e-mail confirmé.
          Les droits sont attribués par le serveur ; aucune récompense n'est créée depuis le navigateur.
        </p>
        {stats && <div className="bo-referral-stats">
          <article><strong>{stats.active_referrers}</strong><small>Parrains actifs</small></article>
          <article><strong>{stats.total_referrals}</strong><small>Inscriptions parrainées</small></article>
          <article><strong>{stats.verified_referrals}</strong><small>Filleuls validés</small></article>
          <article><strong>{stats.total_rewards}</strong><small>Récompenses attribuées</small></article>
        </div>}
      </div>

      <form className="bo-panel" onSubmit={save}>
        <h3>Règles du programme</h3>
        <div className="bo-referral-grid">
          <label className="bo-referral-toggle">
            <input type="checkbox" checked={form.enabled} onChange={(e) => setField('enabled', e.target.checked)} />
            Programme actif
          </label>
          <label className="bo-referral-toggle">
            <input type="checkbox" checked={form.repeatable} onChange={(e) => setField('repeatable', e.target.checked)} />
            Récompenses renouvelables à chaque palier
          </label>
          <label>Nombre de filleuls requis
            <input className="bo-input" type="number" min="1" max="1000" required
              value={form.requiredSignups} onChange={(e) => setField('requiredSignups', Number(e.target.value))} />
          </label>
          <label>Durée de chaque récompense (jours)
            <input className="bo-input" type="number" min="1" max="365" required
              value={form.rewardDays} onChange={(e) => setField('rewardDays', Number(e.target.value))} />
          </label>
          <label>Contenus débloqués
            <select className="bo-input" value={form.scope} onChange={(e) => setField('scope', e.target.value)}>
              <option value="all">Tous les contenus Premium</option>
              <option value="selected">Uniquement les contenus sélectionnés</option>
            </select>
          </label>
        </div>

        {form.scope === 'selected' && (
          <div className="bo-referral-select">
            <h4>Sélectionner les contenus Premium ({form.selectedItems.length})</h4>
            <p className="bo-muted">La liste ne contient que les contenus Premium publiés et actifs.</p>
            <input className="bo-input" aria-label="Rechercher un contenu Premium"
              value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher un cours, un quiz, une simulation…" />
            {catalog.length === 0 && <p className="bo-muted">Aucun contenu Premium publié pour le moment.</p>}
            <div className="bo-referral-list">
              {filtered.map((item) => (
                <label key={item.key} className="bo-referral-item">
                  <input type="checkbox" checked={form.selectedItems.includes(item.key)}
                    onChange={(e) => toggleItem(item.key, e.target.checked)} />
                  <span><strong>{item.title}</strong><small>{item.category}</small></span>
                </label>
              ))}
            </div>
          </div>
        )}
        <p className="bo-muted">Les modifications s'appliquent aux récompenses futures. Les avantages déjà attribués sont conservés.</p>
        <div className="bo-referral-actions">
          <button type="button" className="bo-button bo-button--ghost" disabled={busy}
            onClick={() => { setForm(initial); setError(''); setNotice(''); }}>Annuler les modifications</button>
          <button type="submit" className="bo-button bo-button--primary" disabled={busy}>
            {busy ? 'Enregistrement…' : 'Enregistrer les règles'}
          </button>
        </div>
        {error && <p role="alert" className="bo-referral-error">{error}</p>}
        {notice && <p role="status" className="bo-referral-success">{notice}</p>}
      </form>
    </section>
  );
}
